import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import map from 'lodash-es/map'

import Button from '../../../../Button'
import {
  DOCUMENT_UPLOAD_DESTINATION_CURRENT,
  DocumentUploadDestinationModal,
  isDocumentUploadDestinationAll,
} from '../../../../Document'
import { useDocumentDuplicateToRelated } from '../../../../Request'
import { ButtonTextSelectControl } from '../../../../Select'
import { getRequestProfileTabDocumentsUploadOptions } from '../../RequestProfileTabDocuments.utils'

import UploadIcon from '../../../../../icons/upload-1.svg'

export function useRequestProfileTabDocumentsUpload({
  className,
  relatedRequestsCount,
  requestData,
}) {
  const { t } = useTranslation()

  const uploadDestinationRef = useRef(null)

  const skipDestinationModalRef = useRef(false)

  function setUploadDestination(value) {
    uploadDestinationRef.current = value
  }

  const duplicateToRelated = useDocumentDuplicateToRelated({
    requestId: requestData?.id,
  })

  const canAddFileToRelatedRequests =
    requestData?.permissions?.can_add_file_to_related_requests

  const uploadOptions = getRequestProfileTabDocumentsUploadOptions({
    canAddFileToRelatedRequests,
    relatedRequestsCount,
    t,
  })

  function handleAfterUpload(acceptedFiles) {
    const destination = uploadDestinationRef.current

    uploadDestinationRef.current = null
    skipDestinationModalRef.current = false

    if (isDocumentUploadDestinationAll(destination)) {
      duplicateToRelated(map(acceptedFiles, 'id'))
    }
  }

  function handleSelectUploadOption(option) {
    if (option?.value) {
      skipDestinationModalRef.current = true
    }

    setUploadDestination(option?.value)
  }

  function renderUploadDestinationModal({ onClose, onConfirm }) {
    const shouldSkipModal = skipDestinationModalRef.current

    skipDestinationModalRef.current = false

    if (shouldSkipModal) {
      return null
    }

    uploadDestinationRef.current = null

    function handleSubmit(value) {
      setUploadDestination(value)
      onConfirm()
    }

    const selectableOptions = uploadOptions.filter(option => !option.disabled)

    if (selectableOptions.length <= 1) {
      setUploadDestination(DOCUMENT_UPLOAD_DESTINATION_CURRENT)
      onConfirm()

      return null
    }

    return (
      <DocumentUploadDestinationModal
        options={selectableOptions}
        onClose={onClose}
        onSubmit={handleSubmit}
      />
    )
  }

  const uploadButtonProps = {
    Control: ButtonTextSelectControl,
    className,
    controlProps: {
      Button: Button.Text,
      buttonProps: {
        beforeIconProps: { Icon: UploadIcon },
        text: t('RequestProfileTabDocuments.UploadBtnKey'),
      },
    },
    dropdownFloatingConfig: { maxWidthByRef: false },
    listProps: {
      headerProps: { Search: null },
      itemProps: {
        contentProps: {
          infoProps: {
            contentTextProps: {
              textProps: { isSecondary: true },
            },
          },
        },
      },
    },
    options: uploadOptions,
    onSelectItem: handleSelectUploadOption,
  }

  return {
    handleAfterUpload,
    renderUploadDestinationModal,
    uploadButtonProps,
  }
}
